// ---------------------------------------------------------------------------
// Reports — the "custom report" surface.
// ---------------------------------------------------------------------------
// Pat: "Is there a custom report to be able to search through events by keyword
// or whatever." Keyword + project + person + status + date range, over everything
// you can reach, with a spreadsheet export of exactly the rows on screen.
//
// A full-screen overlay (the app has no router), so it also owns a visible way
// back — the Inbox taught us that lesson.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReportPage, ReportHit, ReportParams, ReportDateBasis } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { nav } from '@/core/router/navIntent';
import { useUiStore } from '@/core/ui/uiStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { useToastStore } from '@/core/ui/toastStore';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';
import { Spinner, StateBadge } from '@/core/ui/atoms';
import { Icon } from '@/core/ui/icons';
import { formatDate, relDueLabel } from '@/core/ui/helpers';
import { dueBucket } from '@/features/tasks/grouping';
import { ArrowLeft, BarChart3, Download } from 'lucide-react';

const PAGE = 50;

const CLOSED = new Set(['1_done', '1_canceled']);
// Badge only the states that SAY something (mirrors My Work and the board).
// "In Progress" is the default condition of work — stamping it beside 200 rows
// was volume without signal.
const NOTABLE_STATE = new Set(['02_changes_requested', '03_approved', '04_waiting_normal', '1_canceled']);

/** Local YYYY-MM-DD — the same hand-built local day the date inputs speak.
 *  Never via toISOString(): a UTC round-trip shifts the day anywhere west of UTC. */
function localToday(): string {
  const d = new Date();
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function Snippet({ text, matches }: { text: string; matches?: Array<[number, number]> }) {
  if (!matches?.length) return <>{text}</>;
  const out: React.ReactNode[] = [];
  let at = 0;
  matches.forEach(([s, e], i) => {
    if (s > at) out.push(text.slice(at, s));
    out.push(<mark key={i} className="rounded bg-brand/25 px-0.5 text-ink">{text.slice(s, e)}</mark>);
    at = e;
  });
  if (at < text.length) out.push(text.slice(at));
  return <>{out}</>;
}

const SOURCE_LABEL: Record<string, string> = {
  name: 'title', description: 'description', comment: 'comment', tag: 'tag', filter: '',
};

/**
 * One vocabulary of tags as picker options, namespaced the way the server expects
 * ('ct:' content / 'st:' standard) so nobody has to learn that a prefix exists.
 *
 * Tag NAMES are not unique and cannot be treated as keys. Live data has three tags
 * called "Youtube" — two of them content tags — plus a "Website" and an "Internal"
 * in each vocabulary, and the two models have colliding numeric ids besides. The
 * group label separates the vocabularies; a name repeated INSIDE one of them gets
 * its id so the two options are at least distinguishable. Collapsing them into one
 * entry would be the worse bug: the ids hold different tasks (8 and 1 for the two
 * "Youtube" content tags), so a merged option would quietly drop one side's rows.
 */
function tagOptions(tags: Array<{ id: number; name: string }>, ns: 'ct' | 'st'): Array<{ key: string; label: string }> {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const t of tags) {
    const k = t.name.trim().toLowerCase();
    if (seen.has(k)) dupes.add(k); else seen.add(k);
  }
  return tags
    .map((t) => ({
      key: `${ns}:${t.id}`,
      label: dupes.has(t.name.trim().toLowerCase()) ? `${t.name} (#${t.id})` : t.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function ReportsPage() {
  const close = useUiStore((s) => s.closeReports);
  useEscapeToClose(close);
  const areas = useAreaStore((s) => s.areas);
  const selectArea = useAreaStore((s) => s.selectArea);
  const assignees = useLookupStore((s) => s.assignees);
  const contentTags = useLookupStore((s) => s.contentTags);
  const standardTags = useLookupStore((s) => s.standardTags);
  const loadLookups = useLookupStore((s) => s.load);
  const toast = useToastStore((s) => s.show);
  const initialQ = useUiStore((s) => s.reportsQuery);

  const [q, setQ] = useState(initialQ ?? '');
  const [areaId, setAreaId] = useState<number | ''>('');
  // 'none' = unassigned-only. Neither adapter can express "has no assignee" (the
  // route clamps assigneeId to positive-or-null), so that value never reaches the
  // server — rows are faceted after they arrive, and every count on screen says
  // what it actually counted (see `visible` below).
  const [assigneeId, setAssigneeId] = useState<number | '' | 'none'>('');
  // Namespaced 'ct:<id>' / 'st:<id>' — never a bare id. The two Odoo tag models have
  // colliding numeric ids, so a bare one is ambiguous by construction.
  const [tagId, setTagId] = useState('');
  const [states, setStates] = useState<Array<'open' | 'done' | 'cancelled'>>(['open', 'done', 'cancelled']);
  const [dateBasis, setDateBasis] = useState<ReportDateBasis>('created');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchComments, setSearchComments] = useState(true);
  const [includeSubtasks, setIncludeSubtasks] = useState(true);

  const [items, setItems] = useState<ReportHit[]>([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);
  // True between a filter edit and the debounced refetch: paging during that window
  // would append NEW-filter rows at the OLD list's offset.
  const [pendingFilters, setPendingFilters] = useState(false);

  useEffect(() => { void loadLookups(); }, [loadLookups]);

  // One-shot preset from a Home doorway (stat tile / workload row): apply it,
  // then clear it, so the next plain "Reports" open starts from the defaults.
  // Applied in an effect rather than initial state so a doorway clicked while
  // Reports is already open still lands its filters.
  const preset = useUiStore((s) => s.reportsPreset);
  useEffect(() => {
    if (!preset) return;
    if (preset.states) setStates(preset.states);
    if (preset.assigneeId !== undefined) setAssigneeId(preset.assigneeId);
    if (preset.dateBasis) setDateBasis(preset.dateBasis);
    if (preset.dateFrom !== undefined) setDateFrom(preset.dateFrom);
    if (preset.dateTo !== undefined) setDateTo(preset.dateTo);
    useUiStore.getState().clearReportsPreset();
  }, [preset]);

  const ctOpts = useMemo(() => tagOptions(contentTags, 'ct'), [contentTags]);
  const stOpts = useMemo(() => tagOptions(standardTags, 'st'), [standardTags]);

  // "Finished" only exists for work that finished, so the server drops open rows for
  // that basis no matter what the chips say. Rather than let a ticked Open chip
  // promise rows the report can never return, Open goes off (and unavailable) while
  // that basis is selected — and the raw selection is kept untouched underneath, so
  // switching the basis back restores exactly what they had.
  const closedBasis = dateBasis === 'closed';
  const effStates = useMemo(
    () => (closedBasis ? states.filter((s) => s !== 'open') : states),
    [closedBasis, states],
  );

  // The unassigned facet (see the assigneeId comment): filter the fetched rows,
  // never pretend the server did it.
  const unassignedOnly = assigneeId === 'none';
  const visible = useMemo(
    () => (unassignedOnly ? items.filter((h) => h.assignees.length === 0) : items),
    [unassignedOnly, items],
  );

  // Broken down BY PROJECT (Allyson, 8/10: "Is it broken down by what — just a
  // long list…"). Groups keep the server's sort: a group appears where its
  // newest row would have, rows inside stay in fetched order. The row index
  // rides along because taskId alone is not unique (one task can hit in both
  // its title and a comment).
  const grouped = useMemo(() => {
    const map = new Map<number, { areaName: string; rows: Array<{ h: ReportHit; i: number }> }>();
    visible.forEach((h, i) => {
      let g = map.get(h.areaId);
      if (!g) { g = { areaName: h.areaName, rows: [] }; map.set(h.areaId, g); }
      g.rows.push({ h, i });
    });
    return [...map.values()];
  }, [visible]);

  const qs = useCallback((extra: Partial<ReportParams> = {}): string => {
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    if (areaId) p.set('areaIds', String(areaId));
    if (assigneeId && assigneeId !== 'none') p.set('assigneeId', String(assigneeId));
    if (tagId) p.set('tagId', tagId);
    // Send the states param whenever the selection is not ALL THREE. With zero
    // selected we never fetch at all (see `noStates` below) - omitting the param made
    // an empty selection silently mean 'every status', the opposite of what the
    // chips showed.
    if (effStates.length < 3) p.set('states', effStates.join(','));
    p.set('dateBasis', dateBasis);
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    p.set('searchComments', String(searchComments));
    p.set('includeSubtasks', String(includeSubtasks));
    if (extra.offset !== undefined) p.set('offset', String(extra.offset));
    if (extra.limit !== undefined) p.set('limit', String(extra.limit));
    return p.toString();
  }, [q, areaId, assigneeId, tagId, effStates, dateBasis, dateFrom, dateTo, searchComments, includeSubtasks]);

  // Filters change fast; only the newest response may write state.
  const seq = useRef(0);
  const run = useCallback(async (reset: boolean, offset: number) => {
    const my = ++seq.current;
    setLoading(true);
    try {
      // The unassigned facet throws away assigned rows after they arrive, so ask
      // for the server's max page to keep "Load more" clicks proportionate.
      const page = await api.get<ReportPage>(`/reports/search?${qs({ offset, limit: unassignedOnly ? 100 : PAGE })}`);
      if (my !== seq.current) return;
      setError(false);
      setTotal(page.approxTotal);
      setTruncated(page.truncated);
      setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
    } catch {
      if (my === seq.current) { setError(true); if (reset) { setItems([]); setTotal(0); } }
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [qs, unassignedOnly]);

  // Nothing selected = nothing to show. Skip the request rather than letting the
  // server's "no param means all" default contradict the chips.
  const noStates = effStates.length === 0;
  // An inverted range yields a contradictory domain and zero rows; say so instead of
  // rendering the generic "pick some filters" idle copy.
  const badRange = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  // Home's "Overdue" tile and this page's Overdue preset measure DIFFERENT things,
  // on purpose — decomposed against the live mirror 2026-07-27 (tile 299, here 226):
  //   · the tile counts open work late on EITHER date: past deadline, OR past film
  //     date on the content teams (83 rows had only the film date late) — a report
  //     filters ONE date basis per query, so it cannot express that OR;
  //   · this report includes ARCHIVED rows the tile skips (10 rows here) — archived
  //     hits belong in every report and wear their own chip.
  // 226 − 10 + 83 = 299, exactly. Rather than quietly bend either number, the count
  // wears a caption saying which measure this is. Keyed off the filters themselves
  // ("open work, late by deadline"), not off how they were reached — typed-in
  // filters that mean the same thing earn the same honesty.
  const overdueScope = !unassignedOnly
    && dateBasis === 'deadline'
    && effStates.length === 1 && effStates[0] === 'open'
    && !dateFrom && Boolean(dateTo) && dateTo < localToday();

  // A failure belongs to the query that produced it. Editing the filters retires it —
  // otherwise the banner (and its Retry) outlive the result set they describe.
  useEffect(() => { setPendingFilters(true); setError(false); }, [qs, noStates]);

  useEffect(() => {
    if (noStates) { seq.current++; setItems([]); setTotal(0); setTruncated(false); setLoading(false); setError(false); return; }
    const t = setTimeout(() => { setPendingFilters(false); void run(true, 0); }, 300);
    return () => clearTimeout(t);
  }, [run, noStates]);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Through the api client so the JWT goes with it — a bare <a href> would 401.
      const blob = await api.getBlob(`/reports/export.csv?${qs()}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cg-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not build the spreadsheet.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const toggleState = (s: 'open' | 'done' | 'cancelled') =>
    setStates((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const ctrl = 'h-8 rounded-md border border-line bg-canvas px-2 text-xs text-ink outline-none focus:border-brand';
  const chip = (on: boolean) =>
    `rounded px-2 py-0.5 text-[11px] font-medium transition ${on ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`;

  return (
    <div className="fixed inset-0 z-[55] flex flex-col bg-page animate-fade-in">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3 sm:gap-3 sm:px-4">
        <button onClick={close} title="Back (Esc)" className="flex shrink-0 items-center gap-1 rounded-md border border-line px-2 py-1 text-xs font-medium text-muted transition hover:bg-hover hover:text-ink">
          <Icon i={ArrowLeft} size={14} /> Back
        </button>
        <span className="hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-ink sm:inline-flex"><Icon i={BarChart3} size={14} /> Reports</span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search words in titles, descriptions and comments…"
          className="h-8 w-24 min-w-[6rem] flex-1 rounded-md border border-line bg-canvas px-3 text-xs text-ink outline-none placeholder:text-faint focus:border-brand sm:max-w-[26rem]"
        />
        <span className="ml-auto hidden shrink-0 flex-col items-end whitespace-nowrap text-xs text-faint sm:flex">
          <span>
            {unassignedOnly
              // A client-side facet has no server total — count the rows on screen,
              // and let '+' say that unloaded pages may hold more.
              ? `${visible.length.toLocaleString()}${items.length < total ? '+' : ''} unassigned`
              : `${truncated ? '~' : ''}${total.toLocaleString()} match${total === 1 ? '' : 'es'}`}
          </span>
          {/* md+: below that the nowrap caption would push the export button out. */}
          {overdueScope && (
            <span
              className="hidden text-[10px] leading-tight md:block"
              title="Home's Overdue tile counts open work late on EITHER its deadline or (for the content teams) its film date, and skips archived work. This report filters by deadline only and includes archived rows — so the two numbers legitimately differ."
            >
              by deadline · Home's tile also counts film dates &amp; skips archived
            </span>
          )}
        </span>
        <button
          onClick={() => void exportCsv()}
          disabled={exporting || pendingFilters || unassignedOnly || (!items.length && !loading)}
          // The CSV is built server-side, which can't split out unassigned work —
          // shipping it anyway would export rows the screen is hiding.
          title={unassignedOnly
            ? 'The spreadsheet can’t split out unassigned work — set the person back to "Anyone" first.'
            : "Downloads this report's full result set for the current filters (up to 2,000 rows)"}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand px-2.5 py-1 text-xs font-semibold text-page transition hover:brightness-110 disabled:opacity-50"
        >
          {exporting ? 'Preparing…' : <><Icon i={Download} size={14} /> Spreadsheet</>}
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        <select value={areaId} onChange={(e) => setAreaId(e.target.value ? Number(e.target.value) : '')} className={`${ctrl} max-w-[12rem]`}>
          <option value="">All my projects</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value === 'none' ? 'none' : e.target.value ? Number(e.target.value) : '')} className={`${ctrl} max-w-[11rem]`}>
          <option value="">Anyone</option>
          <option value="none">Unassigned</option>
          {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        {(ctOpts.length > 0 || stOpts.length > 0) && (
          <select
            value={tagId}
            onChange={(e) => setTagId(e.target.value)}
            title="Filter by tag"
            className={`${ctrl} max-w-[11rem] ${tagId ? 'border-brand bg-brand/10 text-brand' : ''}`}
          >
            <option value="">Any tag</option>
            {/* Grouped, because the same name exists in both vocabularies. The
                labels match what the task panel calls them. */}
            {ctOpts.length > 0 && (
              <optgroup label="Content tags">
                {ctOpts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </optgroup>
            )}
            {stOpts.length > 0 && (
              <optgroup label="Tags">
                {stOpts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </optgroup>
            )}
          </select>
        )}
        <nav className="flex items-center gap-0.5 rounded-md border border-line bg-canvas p-0.5">
          {([['open', 'Open'], ['done', 'Done'], ['cancelled', 'Cancelled']] as const).map(([v, l]) => {
            const unavailable = closedBasis && v === 'open';
            return (
              <button
                key={v}
                onClick={() => toggleState(v)}
                disabled={unavailable}
                title={unavailable ? 'Open work has no finish date — report by Created or Last updated to include it.' : undefined}
                className={`${chip(effStates.includes(v))}${unavailable ? ' cursor-not-allowed opacity-40' : ''}`}
              >
                {l}
              </button>
            );
          })}
        </nav>
        <select value={dateBasis} onChange={(e) => setDateBasis(e.target.value as ReportDateBasis)} className={ctrl}>
          <option value="created">Created</option>
          <option value="deadline">Deadline</option>
          <option value="keyDate">Event / film date</option>
          {/* The one people actually mean by "what did we get done in June". It sits
              next to "Last updated" deliberately: that was the nearest option before
              this existed, and it answered the question wrong. */}
          <option value="closed">Finished</option>
          <option value="updated">Last updated</option>
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={ctrl} title="From" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={ctrl} title="To" />
        <label className="flex cursor-pointer select-none items-center gap-1.5 rounded-md border border-line px-2 py-1 text-[11px] text-muted hover:bg-hover">
          <input type="checkbox" checked={searchComments} onChange={(e) => setSearchComments(e.target.checked)} className="accent-brand" />
          Comments &amp; notes
        </label>
        <label className="flex cursor-pointer select-none items-center gap-1.5 rounded-md border border-line px-2 py-1 text-[11px] text-muted hover:bg-hover">
          <input type="checkbox" checked={includeSubtasks} onChange={(e) => setIncludeSubtasks(e.target.checked)} className="accent-brand" />
          Sub-tasks
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {truncated && items.length > 0 && (
          <div className="mb-2 rounded-md border border-warn/40 bg-warn/10 px-3 py-1.5 text-[11px] text-warn">
            Showing the strongest matches — narrow the words, project or date range to see everything.
          </div>
        )}
        {error && items.length > 0 && (
          <div className="mb-2 flex items-center gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-1.5 text-[11px] text-danger">
            <span>Could not load more.</span>
            {/* Same guard as Load more: paging while a refetch is in flight would
                append the NEW query's rows at the OLD list's offset. */}
            <button
              onClick={() => void run(false, items.length)}
              disabled={loading || pendingFilters}
              className="underline underline-offset-2 hover:text-ink disabled:opacity-50 disabled:no-underline"
            >
              {loading ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}
        {noStates ? (
          <div className="pt-10 text-center text-sm text-faint">
            {closedBasis && states.includes('open')
              // They ticked Open only, then asked to report by finish date. Say which
              // of the two to change instead of the generic "pick a status".
              ? 'Reporting by finish date covers Done and Cancelled work — tick one of those, or report by Created.'
              : 'Pick at least one status to run the report.'}
          </div>
        ) : error && items.length === 0 ? (
          <div className="pt-10 text-center text-sm text-danger">
            Could not run that report.{' '}
            <button onClick={() => void run(true, 0)} disabled={loading} className="underline underline-offset-2 hover:text-ink disabled:opacity-50">
              {loading ? 'Retrying…' : 'Try again'}
            </button>
          </div>
        ) : loading && items.length === 0 ? (
          <div className="flex h-full items-center justify-center"><Spinner label="Running report…" /></div>
        ) : items.length === 0 ? (
          <div className="pt-10 text-center text-sm text-faint">
            {badRange
              ? 'The "from" date is after the "to" date — swap them to see results.'
              : q.trim() ? 'No matches — try fewer words, or widen the filters.' : 'Pick some filters, or type a word to search.'}
          </div>
        ) : (
          <>
            {grouped.map((g) => (
            <section key={g.rows[0].h.areaId} className="mb-5">
              {/* One header per project, sticky so long groups keep their name in
                  view. This is the answer to "is it broken down by what" — and it
                  carries the project name, so the rows don't have to repeat it. */}
              <div className="sticky top-0 z-10 -mx-4 mb-1.5 border-b border-line bg-page px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted">
                {g.areaName} <span className="font-normal normal-case text-faint">· {g.rows.length}</span>
              </div>
              {g.rows.map(({ h, i }) => {
              // My Work's fixed-column recipe: how-late (toned), the absolute date
              // (faint, year-honest), a badge slot — same widths, so 200 rows scan
              // as a table instead of a ragged card edge.
              //
              // COLUMNS CLUSTER LEFT AT lg+. On a big monitor a flex-1 title
              // pushed the date to the far edge of a 2000px row — "why is the
              // date so far in the corner" (Allyson, 8/10). Capping the title
              // cell pulls stage, people and dates in right next to the words
              // they belong to; below lg the old stacked shape (title flexes,
              // dates right) is still correct for the narrow screen.
              //
              // THE DATE COLUMN FOLLOWS THE CHOSEN BASIS. It used to show only
              // deadline/key-date, so a report on "Created" over Accounting's
              // mostly-undated cards rendered a page of blank date cells —
              // "Why do none of these have dates" (Allyson, 7/28). Created and
              // closed always exist for their rows; lateness stays a
              // deadline-world concept and disappears on those bases.
              const due = h.deadline ?? h.keyDate;
              const dateLed = dateBasis === 'created' ? h.createdAt
                : dateBasis === 'closed' ? h.closedAt
                : due;
              const showLate = dateBasis !== 'created' && dateBasis !== 'closed';
              const rel = showLate && due ? relDueLabel(due) : '';
              const abs = dateLed ? formatDate(dateLed.slice(0, 10)) : '—';
              const bucket = showLate ? dueBucket(due) : null;
              // Lateness is only a live fact for OPEN work — a finished task's past
              // deadline is history, not an alarm.
              const relTone = CLOSED.has(h.state) ? 'text-faint'
                : bucket === 'overdue' ? 'text-danger'
                : bucket === 'today' || bucket === 'week' ? 'text-warn'
                : 'text-faint';
              return (
              <button
                key={`${h.taskId}-${i}`}
                onClick={() => {
                  // Both mutations inside ONE synchronous tx — see SearchModal.pick:
                  // a `.then` continuation runs after tx has already flushed, which
                  // pushes a second history entry and kills the first ✕.
                  const t = useTaskDetailStore.getState();
                  if (!t.confirmOpen(h.taskId)) return;
                  const already = t.openId === h.taskId && t.task !== null;
                  nav.tx('push', () => {
                    if (useAreaStore.getState().selectedAreaId !== h.areaId) selectArea(h.areaId);
                    if (!already) void t.openFromRoute(h.taskId);
                  });
                  close();
                }}
                className="mb-1.5 flex w-full items-start gap-2 rounded-lg border border-line bg-canvas p-2.5 text-left transition hover:border-(--color-line-strong) hover:bg-hover"
              >
                <span className="min-w-0 flex-1 lg:w-[42%] lg:max-w-[640px] lg:flex-none">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 truncate text-[13px] text-ink">{h.name}</span>
                    {h.archived && <span className="shrink-0 rounded border border-line px-1 text-[10px] text-faint">archived</span>}
                    {/* Stage inline only below lg — above, it has its own column. */}
                    {h.stageName && <span className="shrink-0 text-[11px] text-faint lg:hidden">{h.stageName}</span>}
                  </span>
                  {h.snippet && (
                    <span className="mt-0.5 block text-[11px] leading-snug text-muted">
                      {h.source === 'comment' && (
                        <span className="text-faint">
                          {h.commentAuthor}{h.isNote ? ' (note)' : ''}:{' '}
                        </span>
                      )}
                      <Snippet text={h.snippet} matches={h.matches} />
                    </span>
                  )}
                  <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] text-faint">
                    {SOURCE_LABEL[h.source] && <span>matched in {SOURCE_LABEL[h.source]}</span>}
                    {h.assignees.length > 0 && <span className="lg:hidden">· {h.assignees.join(', ')}</span>}
                    {h.closedAt && <span>· finished {h.closedAt.slice(0, 10)}</span>}
                  </span>
                </span>
                <span className="hidden w-[150px] shrink-0 truncate pt-0.5 text-[11px] text-faint lg:block">{h.stageName}</span>
                <span className="hidden w-[200px] shrink-0 truncate pt-0.5 text-[11px] text-faint lg:block">
                  {h.assignees.length === 0 ? '—'
                    : h.assignees.length <= 2 ? h.assignees.join(', ')
                    : `${h.assignees.slice(0, 2).join(', ')} +${h.assignees.length - 2}`}
                </span>
                <span className={`w-[72px] shrink-0 text-right text-[11px] tabular-nums ${relTone} lg:pt-0.5`}>{rel}</span>
                <span className="hidden w-[70px] shrink-0 text-right text-[11px] tabular-nums text-faint sm:block lg:pt-0.5">{rel !== abs ? abs : ''}</span>
                <span className="flex w-[74px] shrink-0 justify-end">
                  {NOTABLE_STATE.has(h.state) && <StateBadge state={h.state} />}
                </span>
              </button>
              );
              })}
            </section>
            ))}
            {unassignedOnly && visible.length === 0 && (
              <div className="pt-10 pb-2 text-center text-sm text-faint">
                {items.length < total
                  ? `None of the ${items.length.toLocaleString()} matches loaded so far are unassigned — load more to keep scanning.`
                  : 'Every match here is assigned to someone.'}
              </div>
            )}
            {items.length < total && (
              <button
                onClick={() => void run(false, items.length)}
                disabled={loading || pendingFilters}
                className="mx-auto mt-2 block rounded-md border border-line px-3 py-1.5 text-xs text-muted transition hover:bg-hover hover:text-ink disabled:opacity-50"
              >
                {loading ? 'Loading…' : `Load more (${(total - items.length).toLocaleString()} left)`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
